import cx from 'classnames'
import { useTranslation } from 'next-i18next'
import { numberToCurrency } from 'utils'

import { Product } from '../../hooks/useAcreCalculator'
import {
  PaymentExtras,
  roundHalfUp,
  roundedExtras,
} from './MonthlyPaymentBreakdown'

/**
 * Make-offer's single-column "Monthly payment breakdown" card for the selected
 * product: base, taxes, insurance and HOA, the total, and the saving against a
 * mortgage carrying the same extras. `total` is printed as given, never
 * re-summed, so it is always the figure the offer records.
 */
const PaymentBreakdownCard = ({
  product,
  productBase,
  total,
  mortgageBase,
  extras,
}: {
  product: Product
  productBase: number
  total: number
  mortgageBase: number
  extras: PaymentExtras
}) => {
  const { t } = useTranslation('homes')
  const { tax, insurance, hoa } = roundedExtras(extras)
  const savings = roundHalfUp(mortgageBase) + tax + insurance + hoa - total

  const ruleRow = cx(
    'border-b',
    product === 'saver' ? 'border-teal-3' : 'border-tan-3'
  )
  const label = 'py-12 pl-12 text-left align-top text-14 font-normal leading-5'
  const figure =
    'whitespace-nowrap py-12 pl-16 pr-12 text-right align-top text-16 font-semibold leading-5'
  const notes = [
    {
      label: t('paymentBreakdown.variableCostsLabel'),
      text: t('paymentBreakdown.variableCostsText'),
    },
    {
      label: t('paymentBreakdown.cappedLabel'),
      text: t('paymentBreakdown.cappedText'),
    },
  ]

  return (
    <div className="mt-24 rounded-md border border-green-1 bg-white p-24">
      <h3 className="text-18 font-semibold leading-6">
        {t('paymentBreakdown.heading')}
      </h3>
      {/* Rules and the total band run 12px past the text on each side. */}
      <table className="-mx-12 w-[calc(100%+24px)]">
        <tbody>
          <tr className={ruleRow}>
            <th scope="row" className={label}>
              {t('paymentBreakdown.basePayment')}
              <span className="mt-2 block text-12 leading-[15px] text-grey-7">
                {t('paymentBreakdown.fixedTerm')}
              </span>
            </th>
            <td className={figure}>{numberToCurrency(productBase)}</td>
          </tr>
          <tr>
            <th scope="row" className={label}>
              {t('paymentBreakdown.propertyTaxes')}
            </th>
            <td className={figure}>{numberToCurrency(tax)}</td>
          </tr>
          <tr className={cx({ [ruleRow]: !extras.showHoa })}>
            <th scope="row" className={label}>
              {t('paymentBreakdown.insurance')}
            </th>
            <td className={figure}>{numberToCurrency(insurance)}</td>
          </tr>
          {extras.showHoa && (
            <tr className={ruleRow}>
              <th scope="row" className={label}>
                {t('paymentBreakdown.hoaFees')}
              </th>
              <td className={figure}>{numberToCurrency(hoa)}</td>
            </tr>
          )}
        </tbody>
      </table>
      <div
        className={cx(
          '-mx-12 flex items-start justify-between gap-16 px-12 py-16',
          product === 'saver' ? 'bg-teal-1' : 'bg-tan-1'
        )}
      >
        <span className="text-16 font-semibold leading-8">
          {t('paymentBreakdown.totalPayment')}
        </span>
        <span className="text-right text-24 font-semibold leading-8">
          <span data-testid="payment-breakdown-total">
            {numberToCurrency(total)}
          </span>
          {savings > 0 && (
            <span
              className={cx(
                'mt-[3px] block text-14 font-normal italic leading-5',
                product === 'saver' ? 'text-teal-3' : 'text-[#A46442]'
              )}
            >
              {t('paymentBreakdown.save', {
                amount: numberToCurrency(savings),
              })}
            </span>
          )}
        </span>
      </div>
      <ul className="mt-8">
        {notes.map((note) => (
          <li
            key={note.label}
            className="pt-16 text-14 leading-5 [@media(min-width:480px)]:flex [@media(min-width:480px)]:gap-12"
          >
            <span className="block font-semibold [@media(min-width:480px)]:shrink-0 [@media(min-width:480px)]:grow-0 [@media(min-width:480px)]:basis-[104px]">
              {note.label}
            </span>
            <span>{note.text}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default PaymentBreakdownCard
